import { AfterViewInit, Directive, inject, OnInit } from '@angular/core';
import { FormBuilder, ValidatorFn, Validators } from '@angular/forms';

import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { DisplaySchema, SummitIdObject, ViewField } from '@summit/models';
import { FormFunctions } from '@summit/services';
import { ApiError } from '@summit/ui';
import { DialogEditConfig } from './dialog-edit-config';
import { SummitFormBase } from './summit-form-base';

@Directive()
export abstract class SummitEditDialogPrime<T extends SummitIdObject>
  extends SummitFormBase
  implements OnInit, AfterViewInit
{
  element?: T;
  abstract schema: DisplaySchema<T>;
  override apiError: ApiError = new ApiError();

  disabledFields?: (keyof T)[];
  hideFields?: (keyof T)[];

  formBuilder: FormBuilder;
  dialogRef: DynamicDialogRef;
  dialogConfig: DynamicDialogConfig;
  configData: DialogEditConfig<T>;

  constructor() {
    super();
    this.formBuilder = inject(FormBuilder);
    this.dialogRef = inject(DynamicDialogRef);
    this.dialogConfig = inject(DynamicDialogConfig);
    this.configData = this.dialogConfig.data;

    // If Disabling is not working, then the Dialog Component isn't passing dialogEditConfig in the constructor
    if (this.configData) {
      this.disabledFields = this.configData.disabledFields ?? [];
      this.hideFields = this.configData.hideFields ?? [];
    }
  }

  /**copied from EBO-Edit */

  buildFormGroup(formGroupParameters: any, entries: ViewField<T>[]) {
    entries.forEach((entry: ViewField<T>) => {
      if (entry.formField) {
        this.addFormControlConfig(formGroupParameters, entry);
      } else if (entry.children) {
        const children = entry.children;
        const childFormGroupParameters = new Object();
        this.buildFormGroup(
          childFormGroupParameters,
          Object.keys(children).map((key) => children[key]) as ViewField<T>[]
        );
        console.log(`Adding Group ${entry.propertyName.toString()} with `, childFormGroupParameters);
        formGroupParameters[entry.propertyName] = this.formBuilder.group(childFormGroupParameters);
        //TODO: THIS COULD CAUSE ISSUES IF THERE ARE NESTED GROUPS, NEED TO MOVE THIS TO A SINGLE OBJECT WITH SUB OBJECTS
        this[entry.propertyName as keyof this] = formGroupParameters[entry.propertyName];
      }
    });
  }
  addFormControlConfig(formGroupParameters: any, entry: ViewField<T>) {
    // Build Validators Array
    const validatorsArray: ValidatorFn[] = [];
    if (entry.validationMessages) {
      entry.validationMessages
        .filter((validation) => !validation.custom)
        .forEach((validation) => {
          switch (validation.type) {
            case 'required':
              validatorsArray.push(Validators.required);
              break;
            case 'min':
              validatorsArray.push(Validators.min(validation.parameter as number));
              break;
            case 'max':
              validatorsArray.push(Validators.max(validation.parameter as number));
              break;
            case 'minlength':
              validatorsArray.push(Validators.minLength(validation.parameter as number));
              break;
            case 'nullValidator':
              validatorsArray.push(Validators.nullValidator);
              break;
            case 'pattern':
              validatorsArray.push(Validators.pattern(validation.parameter as RegExp));
              break;
            default:
              console.error(`Unsupported validation Type ${validation.type} on ${this.schema.refTitle}`);
          }
        });
    }
    // console.log(`Adding control ${entry.propertyName}`);
    if (validatorsArray.length > 0) {
      // formGroupParameters[entry.propertyName] = ['', validatorsArray];
      formGroupParameters[entry.propertyName] = this.formBuilder.control('', Validators.compose(validatorsArray));
    } else {
      // formGroupParameters[entry.propertyName] = [''];
      formGroupParameters[entry.propertyName] = this.formBuilder.control('');
    }
  }

  /** End direct copy */

  ngOnInit(): void {
    this.element = this.schema.NewObject(this.configData?.element);
    const formGroupParameters = new Object();
    this.buildFormGroup(formGroupParameters, this.schema.getFormAttributeList());
    // console.log(`FormGroup config is built`, formGroupParameters);
    this.elementForm = this.formBuilder.group(formGroupParameters);
    // console.log(`elementForm:`, this.elementForm);
    this.updateForm();
    if (this.disabledFields) {
      this.disabledFields.forEach((field) => {
        //console.log(`Disabling: ${field}`);
        const control = FormFunctions.FindFormControl(this.elementForm, field as string);
        if (control) {
          control.disable();
        }
      });
    }
  }

  ngAfterViewInit(): void {
    if (this.disabledFields) {
      this.disabledFields.forEach((field) => {
        //console.log(`Disabling: ${field}`);
        const control = FormFunctions.FindFormControl(this.elementForm, field as string);
        if (control) {
          control.disable();
        }
      });
    }

    // Enable validators after initialization completes
    // Components that override ngAfterViewInit with async initialization should call finishInitialization() themselves
    setTimeout(() => {
      if (this.isInitializing) {
        this.finishInitialization();
      }
    }, 0);
  }
  protected updateForm() {
    const newValues = Object.assign({}, this.element);
    //console.log('Patching with:', newValues);
    // Use { emitEvent: false } to prevent triggering validation/valueChanges loops during form initialization
    if (this.element && this.elementForm) {
      this.elementForm.patchValue(newValues);
    }
  }

  confirm(): void {
    if (this.preSaveCheck() === false) {
      return;
    }
    this.dialogRef.close(this.schema.NewObject(this.elementForm.value));
  }

  close($event?: Event) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    this.dialogRef.close();
  }
}
